import React from "react";
import { usePlayer } from "@/context/PlayerContext";
import { Play, Pause, X, Music2 } from "lucide-react";
import { Link } from "react-router-dom";

const fmt = (s) => {
  if (!s || isNaN(s)) return "0:00";
  const m = Math.floor(s / 60);
  const sec = Math.floor(s % 60).toString().padStart(2, "0");
  return `${m}:${sec}`;
};

export default function StickyPlayer() {
  const { current, playing, toggle, progress, duration, t, mode, seek, stop } = usePlayer();
  if (!current) return null;

  const cap = mode === "preview" ? Math.min(30, duration || 30) : duration;
  const displayT = mode === "preview" ? Math.min(t, 30) : t;

  return (
    <div className="fixed bottom-0 inset-x-0 z-50 player-glass" data-testid="sticky-player">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 flex items-center gap-4">
        <Link to={`/songs/${current.id}`} className="flex items-center gap-3 min-w-0 w-56">
          <div className="w-11 h-11 rounded-lg overflow-hidden shrink-0 bg-neutral-200">
            <img src={current.cover_url} alt={current.title} className="w-full h-full object-cover" />
          </div>
          <div className="min-w-0">
            <div className="text-sm font-medium truncate">{current.title}</div>
            <div className="text-xs text-neutral-500 truncate flex items-center gap-1.5">
              <Music2 size={11} strokeWidth={1.5} /> {current.artist}
              {mode === "preview" && <span className="text-[10px] tracking-[0.2em] uppercase text-[--sanctum-accent] ml-1">Preview</span>}
            </div>
          </div>
        </Link>

        <button
          data-testid="sticky-toggle"
          onClick={toggle}
          className="w-11 h-11 rounded-full bg-neutral-900 text-white hover:bg-black flex items-center justify-center shrink-0"
          aria-label={playing ? "Pause" : "Play"}
        >
          {playing ? <Pause size={16} strokeWidth={1.5} /> : <Play size={16} strokeWidth={1.5} className="ml-0.5" />}
        </button>

        <div className="flex-1 min-w-0 flex items-center gap-3">
          <span className="text-[10px] text-neutral-500 tabular-nums w-9 text-right">{fmt(displayT)}</span>
          <div
            className="relative flex-1 h-1 bg-neutral-200 rounded-full cursor-pointer group"
            onClick={(e) => {
              const rect = e.currentTarget.getBoundingClientRect();
              seek((e.clientX - rect.left) / rect.width);
            }}
          >
            <div
              className="absolute inset-y-0 left-0 bg-neutral-900 rounded-full"
              style={{ width: `${Math.min(100, progress * 100)}%` }}
            />
          </div>
          <span className="text-[10px] text-neutral-500 tabular-nums w-9">{fmt(cap)}</span>
        </div>

        <button
          data-testid="sticky-close"
          onClick={stop}
          className="p-2 text-neutral-400 hover:text-neutral-900"
          aria-label="Close player"
        >
          <X size={16} strokeWidth={1.5} />
        </button>
      </div>
    </div>
  );
}
